import { Autocomplete, Box, Button, Checkbox, FormControlLabel, Paper, TextField, Typography } from "@mui/material";
import { AbstractTypedDataForm } from "../../components/data/DataForm";
import { FormTextInput } from "../../components/form/TextInput";
import { Post } from "../../models/Types";
import { isMobile } from "../../utils";
import Proxy, { root } from "../../models/Proxy";
import ImageInput from "../../components/ImageInput";
import { getTenantId } from "../../models/Tenant";

const w = window as any;

export function postParse(d?: any) {
    const dto = (d || {}) as Post;
	d?.CreateDate && (dto.CreateDate = new Date(d.CreateDate));
	dto.HashTags || (dto.HashTags = []);
    return dto;
}

var editor: any;

export class PostForm extends AbstractTypedDataForm<Post, {}, { tags: string[] }> {
	storyFormData?: FormData

	componentDidMount() {
		super.componentDidMount();

		Proxy.get('PostTags').then(x => this.setState({ tags: x.result }));

		if (!w.CKEDITOR) {
			const script = document.createElement("script");
			script.src = "https://cdn.ckeditor.com/ckeditor5/43.3.1/ckeditor5.umd.js";
			script.async = true;
			script.onload = () => this.forceUpdate();

			document.body.appendChild(script);
		}
	}

	componentWillUnmount() {
		super.componentWillUnmount();

		editor?.destroy();
	}

    protected getTypeId(): string {
        return 'Post';
	}

    protected getTypeName(): React.ReactNode {
        return 'Новость';
    }

    buildButtons() {
        return [];
    }

    protected createEntity() {
        return postParse();
	}

	onEditorRef(e: any, entity?: Post | undefined) {
		editor?.destroy();


		if (!e || !w.CKEDITOR || !entity) { 
			return;
		}

		const {
			AccessibilityHelp,
			AutoImage,
			Autoformat,
			Autosave,
			BlockQuote,
			Bold,
			CKBox,
			CKBoxImageEdit,
			ClassicEditor,
			CloudServices,
			Essentials,
			Heading,
			ImageBlock,
			ImageCaption,
			ImageInline,
			ImageInsert,
			ImageInsertViaUrl,
			ImageResize,
			ImageStyle,
			ImageTextAlternative,
			ImageToolbar,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			LinkImage,
			List,
			ListProperties,
			MediaEmbed,
			Paragraph,
			PasteFromOffice,
			PictureEditing,
			SelectAll,
			Table,
			TableCaption,
			TableCellProperties,
			TableColumnResize,
			TableProperties,
			TableToolbar,
			TextTransformation,
			TodoList,
			Underline,
			Undo
		} = (w.CKEDITOR);

		ClassicEditor
			.create(e, {
				plugins: [
					AccessibilityHelp,
					Autoformat,
					AutoImage,
					Autosave,
					BlockQuote,
					Bold,
					CKBox,
					CKBoxImageEdit,
					CloudServices,
					Essentials,
					Heading,
					ImageBlock,
					ImageCaption,
					ImageInline,
					ImageInsert,
					ImageInsertViaUrl,
					ImageResize,
					ImageStyle,
					ImageTextAlternative,
					ImageToolbar,
					ImageUpload,
					Indent,
					IndentBlock,
					Italic,
					Link,
					LinkImage,
					List,
					ListProperties,
					MediaEmbed,
					Paragraph,
					PasteFromOffice,
					PictureEditing,
					SelectAll,
					Table,
					TableCaption,
					TableCellProperties,
					TableColumnResize,
					TableProperties,
					TableToolbar,
					TextTransformation,
					TodoList,
					Underline,
					Undo
				],
				translations: ['ru'],
				toolbar: {
					items: [
						'undo',
						'redo',
						'|',
						'heading',
						'|',
						'bold',
						'italic',
						'underline',
						'|',
						'link',
						'insertImage',
						'ckbox',
						'mediaEmbed',
						'insertTable',
						'blockQuote',
						'|',
						'bulletedList',
						'numberedList',
						'todoList',
						'outdent',
						'indent'
					],
					shouldNotGroupWhenFull: false
				},
				heading: {
					options: [
						{
							model: 'paragraph',
							title: 'Paragraph',
							class: 'ck-heading_paragraph'
						},
						{
							model: 'heading1',
							view: 'h1',
							title: 'Heading 1',
							class: 'ck-heading_heading1'
						},
						{
							model: 'heading2',
							view: 'h2',
							title: 'Heading 2',
							class: 'ck-heading_heading2'
						},
						{
							model: 'heading3',
							view: 'h3',
							title: 'Heading 3',
							class: 'ck-heading_heading3'
						},
						{
							model: 'heading4',
							view: 'h4',
							title: 'Heading 4',
							class: 'ck-heading_heading4'
						},
						{
							model: 'heading5',
							view: 'h5',
							title: 'Heading 5',
							class: 'ck-heading_heading5'
						},
						{
							model: 'heading6',
							view: 'h6',
							title: 'Heading 6',
							class: 'ck-heading_heading6'
						}
					]
				},
				image: {
					toolbar: [
						'toggleImageCaption',
						'imageTextAlternative',
						'|',
						'imageStyle:inline',
						'imageStyle:wrapText',
						'imageStyle:breakText',
						'|',
						'resizeImage',
						'|',
						'ckboxImageEdit'
					]
				},
				initialData: entity?.Body,
				autosave: {
					save: (editor: any) => this.onChange({
						target: {
							name: 'Body',
							value: editor.getData()
						}
					})
				},
			}).then((x: any) => {
				editor = x;
			});
	}

	save(e: React.FormEvent<HTMLFormElement>) {
		this.onChange({
			target: {
				name: 'Body',
				value: editor.getData()
			}
		});
		
		if (!this.storyFormData) {
			return super.save(e);
		}

		e.preventDefault();
		e.stopPropagation();

		return this.uploadStoryImage(this.state.entity, this.storyFormData).then(() => super.save(e));
	}

	uploadStoryImage(entity: Post | undefined, body: any) {
		return Proxy.postPlain('FileUpload?shared=true', {
			headers: {},
			body
		}).then((res: { result: string[] }) => entity.StoryImage = res.result[0]);
	}

    protected buildItems(entity?: Post | undefined): React.ReactNode {
		return <>
			<Paper sx={isMobile() ? { flex: 1 } : { minWidth: 500, minHeight: 350 }}>
				<Box sx={{ display: 'flex', gap: 2 } }>
					<ImageInput sx={{ borderRadius: 2 }}
						title="Изображение для истории"
						value={entity?.StoryImage ? `${root}files/${entity.StoryImage}` : null}
						onChange={e => {
							const input = e.target as HTMLInputElement;
							const l = input.files?.length;
							if (l) {
								this.storyFormData = new FormData();
								for (var i = 0; i < l; i++) {
									this.storyFormData.append(input.files![i].name, input.files![i]);
								}

								//this.uploadStoryImage(entity, formData);
							} else {
								this.storyFormData = undefined;
								entity && (entity.StoryImage = undefined);
							}
						}} />

					<Box flex={1} display="flex" flexDirection="column" gap={2}>
						<FormTextInput label="Заголовок" name="Name" form={this} entity={entity} />

						<Autocomplete multiple freeSolo
							options={this.state?.tags || []} defaultValue={entity?.HashTags} 
							renderInput={(params) => <TextField {...params} variant="outlined" label="Тэги" name="HashTags" />}
							onChange={(e: any, v) => this.onChange({
								target: {
									name: 'HashTags',
									value: v
								}
							})}
						/>

						<FormControlLabel control={<Checkbox name="ShowInStories" onChange={this.onChange} defaultChecked={entity?.ShowInStories} />} label="Отображать в сторис" />
					</Box>
				</Box>

				<div key="cke">
					<div ref={e => this.onEditorRef(e, entity)} />
				</div> 

				<Box sx={{ display: 'flex' }}>
					{this.buildDeleteButton()}
					<div style={{ flex: 1 }} />
					{<Button variant="contained" type="submit">Сохранить</Button>}
				</Box>
			</Paper>
		</>;
    }
}